"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

type BlogPaginationProps = {
  currentPage: number;
  totalPages: number;
};

export function BlogPagination({
  currentPage,
  totalPages,
}: BlogPaginationProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (totalPages <= 1) return null;

  function buildHref(page: number) {
    const params = new URLSearchParams(searchParams.toString());
    if (page <= 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }

  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <nav className="flex items-center justify-center gap-2" aria-label="Paginación">
      <Button
        variant="outline"
        size="icon"
        className="border-white/10"
        asChild
        disabled={currentPage <= 1}
      >
        <Link href={buildHref(currentPage - 1)} aria-label="Página anterior">
          <ChevronLeft className="h-4 w-4" />
        </Link>
      </Button>

      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`dots-${i}`} className="px-2 text-white/40">
            ...
          </span>
        ) : (
          <Button
            key={p}
            variant={p === currentPage ? "default" : "outline"}
            size="icon"
            className={
              p === currentPage
                ? "bg-red-600 hover:bg-red-700"
                : "border-white/10"
            }
            asChild
          >
            <Link href={buildHref(p)}>{p}</Link>
          </Button>
        ),
      )}

      <Button
        variant="outline"
        size="icon"
        className="border-white/10"
        asChild
        disabled={currentPage >= totalPages}
      >
        <Link href={buildHref(currentPage + 1)} aria-label="Página siguiente">
          <ChevronRight className="h-4 w-4" />
        </Link>
      </Button>
    </nav>
  );
}
